/**
 * Case Question schemas — the firm↔firm channel as CC consumes it (#613, ADR-0150).
 *
 * A Case Question is a typed question→answer pair on a Placed-and-beyond
 * placement. OF→HF one-way; LF is a co-asker with the same closed 3-type menu.
 * Not messaging: no replies, no thread. The store is the portal's `Case
 * Questions` Airtable table, owned entirely by `portal/worker.js`. CC never
 * reads or writes it (ADR-0071 — firm-attributed feeds are portal-only); the CC
 * worker PROXIES the portal's LF-admin case-question doors server-to-server
 * (`/api/admin/case-questions*`), and these schemas pin the door's wire shape.
 *
 * CC sees the ADMIN wire shape: real names + emails (unlike the firm-facing
 * portal embed, which is role-only). Attribution renders from the structured
 * fields; `question` is the canonical sentence the portal derives from the type.
 */

import { z } from "zod";

/** App-side note ceiling — mirrors portal/worker.js CASE_Q_NOTE_MAX so CC
 *  rejects before the proxy hop instead of surfacing the portal's 400 copy. */
export const CASE_QUESTION_NOTE_MAX = 280;

/** The closed 3-topic menu. Mirrors portal/worker.js CASE_Q_TYPES — keep the
 *  two codebases in lockstep (the portal is authoritative on the write). */
export const CASE_QUESTION_TYPES = [
  "Case status",
  "SOL / filing deadline",
  "Settlement & paperwork",
] as const;
export const CaseQuestionTypeSchema = z.enum(CASE_QUESTION_TYPES);
export type CaseQuestionType = z.infer<typeof CaseQuestionTypeSchema>;

/** Type → canonical sentence. Mirrors portal/worker.js CASE_Q_SENTENCES and the
 *  portal.html copy — three strings, three codebases, kept in lockstep. */
export const CASE_QUESTION_SENTENCES: Record<CaseQuestionType, string> = {
  "Case status": "What is the current status of this case?",
  "SOL / filing deadline": "What is the next filing deadline on this case?",
  "Settlement & paperwork":
    "What is the status of settlement and settlement paperwork on this case?",
};

/**
 * Placement decisions at which a Case Question may be asked (Placed-and-beyond).
 * Mirrors portal/worker.js PLACED_OR_LATER_STATUSES — the portal re-checks on
 * write, so this only gates the UI (hide the Ask door on a non-placed case).
 */
export const CASE_QUESTION_ASKABLE_DECISIONS = new Set([
  "Placed",
  "Settled",
  "Case Closed",
]);

/** Whether LF may ask a Case Question on a placement in this decision. */
export function canAskCaseQuestion(decision: string | null | undefined): boolean {
  return decision !== null && decision !== undefined && CASE_QUESTION_ASKABLE_DECISIONS.has(decision);
}

/**
 * One question in the ADMIN wire shape — exactly as the portal door
 * (`shapeAdminQuestions`) projects it. Everything the firm-facing embed carries
 * PLUS the ids and real names/emails CC needs.
 */
export const CaseQuestionSchema = z.object({
  /** Airtable record ID of the Case Questions row. */
  id: z.string(),
  /** Who asked: LF (co-asker) or OF (the originating firm). */
  asker_role: z.enum(["LF", "OF"]),
  question_type: z.string(),
  /** Canonical sentence the portal derived from the type (never stored). */
  question: z.string(),
  /** Optional free-text note the asker added (≤280 chars). */
  note: z.string(),
  /** The HF's answer, or "" while Open. */
  answer: z.string(),
  /** ISO-8601 datetime the question was asked. */
  asked_at: z.string(),
  /** ISO-8601 datetime the HF answered, or "" while Open. */
  answered_at: z.string(),
  status: z.enum(["Open", "Answered"]),
  /** PLACEMENT record id the question is on. Null on a broken link. */
  placement_id: z.string().nullable(),
  /** LEAD record id (denormalized) — the CC case panel + digest group by lead. */
  lead_id: z.string().nullable(),
  /** Client name for the matter. */
  case_label: z.string(),
  of_firm_name: z.string(),
  hf_firm_name: z.string(),
  /** Asker's email (ops-identity email for LF asks; contact email for OF). */
  acting_email: z.string(),
  /** Asker's display name ("LegalFlare" for LF asks; contact name for OF). */
  acting_name: z.string(),
  answered_by_email: z.string(),
  answered_by_name: z.string(),
});
export type CaseQuestion = z.infer<typeof CaseQuestionSchema>;

// ---------------------------------------------------------------------------
// API contracts — /api/operations/case-questions (CC worker proxy routes).
// ---------------------------------------------------------------------------

/**
 * GET /api/operations/case-questions — every question (the Firm↔Firm lane) and
 * GET /api/operations/case-questions/case/:leadId — one matter's questions.
 * One lane, unsorted here — the client narrows + sorts unanswered-first.
 */
export const CaseQuestionsResponseSchema = z.object({
  questions: z.array(CaseQuestionSchema),
});
export type CaseQuestionsResponse = z.infer<typeof CaseQuestionsResponseSchema>;

/**
 * POST /api/operations/case-questions/ask — LF co-asks a handling firm. The
 * portal door (`/api/admin/case-questions/ask`) keys on a PLACEMENT id + the
 * closed type menu; the note is optional (≤280). One open LF question per case
 * (the portal 409s on a second).
 */
export const AskCaseQuestionRequestSchema = z.object({
  placement_id: z.string().min(1),
  question_type: CaseQuestionTypeSchema,
  note: z.string().max(CASE_QUESTION_NOTE_MAX).optional(),
});
export type AskCaseQuestionRequest = z.infer<typeof AskCaseQuestionRequestSchema>;

export const AskCaseQuestionResponseSchema = z.object({
  /** Airtable record id of the new question. */
  question_id: z.string(),
});
export type AskCaseQuestionResponse = z.infer<typeof AskCaseQuestionResponseSchema>;
